import React, { useState, useEffect, useRef } from "react";

export interface Card {
  id: string;
  title: string;
  status: "completed" | "updates-found" | "syncing";
  description?: React.ReactNode;
}

interface AnimatedCardStatusListProps {
  title?: string;
  cards?: Card[];
  onSynchronize?: (cardId: string) => void;
  onAddCard?: () => void;
  onBack?: () => void;
  className?: string;
}

const defaultCards: Card[] = [
  { id: "1", title: "Import products from your store", status: "completed" },
  { id: "2", title: "Unique selling points", status: "completed" },
  { id: "3", title: "Primary customers", status: "completed" },
  { id: "4", title: "Common words & phrases", status: "updates-found" },
  { id: "5", title: "Company overview and offer details", status: "syncing" },
];

// ── CSS injected once ──────────────────────────────────────────────
const STYLE_ID = "card-status-list-styles";

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes csl-fadeInUp {
      from { opacity: 0; transform: translateY(14px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0)    scale(1);    }
    }
    @keyframes csl-spin-dash {
      0%   { transform: rotate(0deg);   }
      100% { transform: rotate(360deg); }
    }
    @keyframes csl-pop-in {
      0%   { opacity: 0; transform: scale(0.6); }
      70%  { opacity: 1; transform: scale(1.12);}
      100% { opacity: 1; transform: scale(1);   }
    }

    .csl-card-enter {
      animation: csl-fadeInUp 0.35s cubic-bezier(0.22,1,0.36,1) both;
    }

    .csl-status-icon-enter {
      animation: csl-pop-in 0.3s cubic-bezier(0.22,1,0.36,1) both;
    }

    .csl-card-inner {
      transition: background 0.2s ease, box-shadow 0.2s ease;
    }
    .csl-card-inner:hover {
      background: rgba(3, 143, 222, 0.06) !important;
      box-shadow: 0 2px 12px rgba(3, 143, 222, 0.08);
    }

    .csl-btn-enter {
      animation: csl-pop-in 0.25s cubic-bezier(0.22,1,0.36,1) both;
    }

    .csl-spinner {
      animation: csl-spin-dash 0.9s linear infinite;
    }

    .csl-scrollbar::-webkit-scrollbar {
      width: 5px;
    }
    .csl-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .csl-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(3, 143, 222, 0.25);
      border-radius: 10px;
    }
    .csl-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(3, 143, 222, 0.45);
    }
  `;
  document.head.appendChild(style);
}

// ── Status icon ────────────────────────────────────────────────────
const CompletedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" className="csl-status-icon-enter flex-shrink-0">
    <circle cx="8" cy="8" r="8" fill="#22c55e" />
    <path
      d="M5 8l2.5 2.5 3.5-4"
      stroke="white"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UpdatesFoundIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" className="csl-status-icon-enter flex-shrink-0">
    <path
      d="M8 1.5L14.5 13H1.5L8 1.5Z"
      fill="#eab308"
      stroke="#eab308"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    <path d="M8 6v3M8 11h0" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SyncingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" className="csl-spinner csl-status-icon-enter flex-shrink-0">
    {Array.from({ length: 8 }).map((_, index) => {
      const angle = index * 45 - 90;
      const radian = (angle * Math.PI) / 180;
      const radius = 6;
      const dashLength = 1.8;
      const startX = 8 + (radius - dashLength / 2) * Math.cos(radian);
      const startY = 8 + (radius - dashLength / 2) * Math.sin(radian);
      const endX = 8 + (radius + dashLength / 2) * Math.cos(radian);
      const endY = 8 + (radius + dashLength / 2) * Math.sin(radian);
      return (
        <line
          key={index}
          x1={startX} y1={startY} x2={endX} y2={endY}
          stroke="#6b7280"
          strokeWidth="2"
          strokeLinecap="round"
          style={{ opacity: (index + 1) / 8 }}
        />
      );
    })}
  </svg>
);

function StatusIcon({ status }: { status: Card["status"] }) {
  switch (status) {
    case "completed":     return <CompletedIcon />;
    case "updates-found": return <UpdatesFoundIcon />;
    case "syncing":       return <SyncingIcon />;
    default:              return null;
  }
}

// ── Single card row ────────────────────────────────────────────────
interface CardRowProps {
  card: Card;
  delay: number;
  onSynchronize?: (id: string) => void;
}

function CardRow({ card, delay, onSynchronize }: CardRowProps) {
  const [hovered, setHovered] = useState(false);
  const [prevStatus, setPrevStatus] = useState(card.status);
  const [statusKey, setStatusKey] = useState(0);

  useEffect(() => {
    if (card.status !== prevStatus) {
      setPrevStatus(card.status);
      setStatusKey((k) => k + 1);
    }
  }, [card.status, prevStatus]);

  const showSyncBtn = card.status === "updates-found" && hovered;

  return (
    <div
      className="csl-card-enter"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className="csl-card-inner relative rounded-xl border overflow-hidden cursor-default"
        style={{
          background: "rgba(255,255,255,0.03)",
          borderColor: "rgba(3, 143, 222, 0.12)",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="relative flex items-start justify-between px-4 py-3 gap-3">
          {/* Left: icon + title + description */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {/* Icon — align to first line of text */}
            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
              <StatusIcon key={statusKey} status={card.status} />
            </div>

            {/* Text block */}
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <span
                className="text-base font-medium leading-snug"
                style={{ color: "rgba(0,0,0,0.82)" }}
              >
                {card.title}
              </span>
              {card.description && (
                <div
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(0,0,0,0.45)" }}
                >
                  {card.description}
                </div>
              )}
            </div>
          </div>

          {/* Right: status badge or sync button */}
          <div className="flex items-center flex-shrink-0 pt-0.5">
            {card.status === "updates-found" ? (
              showSyncBtn ? (
                <button
                  key="sync-btn"
                  className="csl-btn-enter px-2.5 py-1 text-xs font-medium rounded-md border border-current transition-opacity cursor-pointer whitespace-nowrap"
                  onClick={() => onSynchronize && onSynchronize(card.id)}
                  style={{ background: "transparent", color: "#eab308", opacity: 0.85 }}
                >
                  Synchronize
                </button>
              ) : (
                <span className="text-xs font-mono font-semibold tracking-wider whitespace-nowrap px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(234,179,8,0.1)", color: "#ca8a04" }}>
                  UPDATES FOUND
                </span>
              )
            ) : card.status === "syncing" ? (
              <span className="text-xs font-mono font-semibold tracking-wider whitespace-nowrap px-2 py-0.5 rounded-full"
                style={{ background: "rgba(34,197,94,0.1)", color: "#16a34a" }}>
                SYNCING
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────
export function AnimatedCardStatusList({
  title = "Fundamentals",
  cards: initialCards = defaultCards,
  onSynchronize,
  className = "",
}: AnimatedCardStatusListProps = {}) {
  const [cards, setCards] = useState<Card[]>(initialCards);
  const mounted = useRef(false);

  useEffect(() => {
    injectStyles();
    mounted.current = true;
  }, []);

  useEffect(() => {
    setCards(initialCards);
  }, [initialCards]);

  const handleSynchronize = (cardId: string) => {
    if (onSynchronize) { onSynchronize(cardId); }

    setCards((prev) =>
      prev.map((c) => (c.id === cardId ? { ...c, status: "syncing" as const } : c))
    );

    setTimeout(() => {
      setCards((prev) =>
        prev.map((c) => (c.id === cardId ? { ...c, status: "completed" as const } : c))
      );
    }, 2500);
  };

  const sortedCards = [...cards].sort((a, b) => {
    if (a.status === "completed" && b.status !== "completed") return -1;
    if (a.status !== "completed" && b.status === "completed") return 1;
    return 0;
  });

  return (
    <div className={`w-full mx-auto ${className}`}>
      <div className="space-y-2">
        {sortedCards.map((card, i) => (
          <CardRow
            key={card.id}
            card={card}
            delay={i * 55}
            onSynchronize={handleSynchronize}
          />
        ))}
      </div>
    </div>
  );
}
